import type { Exchange, MessageParts, UserMessage } from "@/services/api";
import {
  USER_MESSAGE_LENGTH,
  USER_MESSAGE_START_LENGTH,
  USER_MESSAGE_PREVIEW_LENGTH,
  RESPONSE_PREVIEW_LENGTH,
  TRUNCATE_SUFFIX,
} from "../constants/textLimits";

function openDetailsTag(text: string) {
  return text.replace(/<details(?![^>]*\bopen\b)([^>]*)>/g, "<details open$1>");
}

function cleanPreview(text: string): string {
  return text
    .replace(/^[\s]*$/gm, "")
    .replace(/^---$/gm, "")
    .trim();
}

function escapeQuery(query: string): string {
  return query
    .replace(/[.*+?^${}()|[\]\\"']/g, "\\$&")
    .replace(/`/g, "")
    .trim();
}

export function escapedQueryRegex(
  query: string,
  global: boolean = false,
): RegExp {
  const escapedQuery = escapeQuery(query);
  return new RegExp(`${escapedQuery}`, global ? "gi" : "i");
}

/**
 * Split the exchange into its parts, then highlight the parts if a query is provided
 * @param exchange - The exchange to split
 * @param query - The query to highlight
 * @returns The split exchange parts
 */
export function splitExchangeParts(
  exchange: string,
  query?: string
): MessageParts | null {
  // Normalize line endings and trim
  const normalizedMessage = exchange
    .replace(/\r\n/g, "\n")
    // Replace the [Object object] with _Uncaptured Response_
    .replace(/^\[Object object\]$/, "_Uncaptured response._")
    .trim();

  // Pattern to match the assistant/agent section: _**Assistant**_ or _**Agent (GPT-5)**_
  const responderPattern = /_\*\*(Assistant[^*]*|Agent[^*]*)\*\*_/;

  const responderMatch = normalizedMessage.match(responderPattern);

  if (!responderMatch) {
    return null;
  }

  const responderName = responderMatch[1].trim();
  const responderStartIndex = responderMatch.index! + responderMatch[0].length;

  // Extract user message (between user header and responder header)
  const userMessagesString = normalizedMessage.slice(0, responderMatch.index!);

  const allUserMessages = userMessagesString
    .split(/_\*\*([^*]+)\*\*_/g)
    .slice(1);
  const userMessages: UserMessage[] = [];
  // Remove the first element that is empty:
  allUserMessages.map((message, index) => {
    if (index % 2 === 0) {
      userMessages.push({
        userName: message.trim(),
        userMessage: allUserMessages[index + 1]
          .trim()
          .replace(/---$/g, "") // Sometimes the user prompt is followed by a divider line, we can remove that if it's the last piece of the message
          .trim(), // Remove trailing empty lines and whitespaces
      });
    }
  });

  // Response body (everything AFTER the first responder header) — used for
  // previews and search matching so the agent label doesn't leak into card
  // text previews.
  const responseBody = normalizedMessage.slice(responderStartIndex).trim();
  // Full response INCLUDING the first responder header — used by the session
  // viewer (MarkdownV2) so the first agent turn shows its header and the
  // render-time header dedup has the first occurrence to keep (suppressing
  // subsequent same-identity headers). Without this, the first agent turn
  // after a user prompt rendered with no header.
  const response = normalizedMessage.slice(responderMatch.index).trim();
  const cleanResponse = cleanPreview(responseBody);
  let responsePreview =
    cleanResponse.length > RESPONSE_PREVIEW_LENGTH
      ? cleanResponse.substring(0, RESPONSE_PREVIEW_LENGTH) + TRUNCATE_SUFFIX
      : cleanResponse;

  const cleanUserMessage = cleanPreview(userMessages[0].userMessage);

  // Default user message preview to the first line:
  let userMessagePreview =
    cleanUserMessage.length > USER_MESSAGE_LENGTH
      ? cleanUserMessage.substring(0, USER_MESSAGE_LENGTH) + TRUNCATE_SUFFIX
      : cleanUserMessage;
  if (query) {
    // Say cleanUserMessage has multiple lines, I need to find the first line that matches 'query'
    // and get only that one, substring it to USER_MESSAGE_PREVIEW_LENGTH
    const matchingRegExp = escapedQueryRegex(query);

    const matchingUserMessage = cleanUserMessage.match(matchingRegExp);
    if (matchingUserMessage && matchingUserMessage.index) {
      // I have the starting of the match at matchingUserMessage.index
      // Let's get the line with that index in the middle
      if (matchingUserMessage.index >= USER_MESSAGE_START_LENGTH) {
        // We need to get the line after the match
        const startFrom = Math.max(
          0,
          matchingUserMessage.index - USER_MESSAGE_PREVIEW_LENGTH / 2
        );
        const endAt = startFrom + USER_MESSAGE_PREVIEW_LENGTH;

        userMessagePreview =
          cleanUserMessage.slice(0, USER_MESSAGE_START_LENGTH) +
          TRUNCATE_SUFFIX +
          cleanUserMessage.substring(startFrom, endAt) +
          (endAt < cleanUserMessage.length ? TRUNCATE_SUFFIX : "");
      }
    }

    const matchingResponse = responseBody.match(matchingRegExp);
    if (matchingResponse && matchingResponse.index) {
      if (matchingResponse.index < RESPONSE_PREVIEW_LENGTH) {
        responsePreview =
          responseBody.substring(0, RESPONSE_PREVIEW_LENGTH) + TRUNCATE_SUFFIX;
        responsePreview = cleanPreview(responsePreview);
      } else {
        // We need to get the line after the match
        const startFrom = Math.max(
          0,
          matchingResponse.index - USER_MESSAGE_PREVIEW_LENGTH
        );
        const endAt = startFrom + RESPONSE_PREVIEW_LENGTH;
        responsePreview =
          (startFrom > 0 ? TRUNCATE_SUFFIX : "") +
          responseBody.substring(startFrom, endAt) +
          (endAt < responseBody.length ? TRUNCATE_SUFFIX : "");
        responsePreview = cleanPreview(responsePreview);
      }
    }
  }

  return {
    responderName,
    userMessages,
    userMessagePreview,
    response,
    responsePreview: openDetailsTag(responsePreview),
  };
}

export function highlightExchangeParts(
  exchange: Exchange,
  query: string
): MessageParts | undefined {
  if (!query?.trim()) {
    return exchange.splitExchanges;
  }
  return {
    responderName: exchange.splitExchanges?.responderName || "",
    userMessages:
      exchange.splitExchanges?.userMessages?.map((userMessage: UserMessage) => {
        return {
          userMessage: markTextWithHighlight(userMessage.userMessage, query),
          userName: userMessage.userName,
        };
      }) || [],
    response: markTextWithHighlight(
      exchange.splitExchanges?.response || "",
      query
    ),
    userMessagePreview: markTextWithHighlight(
      exchange.splitExchanges?.userMessagePreview || "",
      query
    ),
    responsePreview: markTextWithHighlight(
      exchange.splitExchanges?.responsePreview || "",
      query
    ),
  };
}

export function markTextWithHighlight(text: string, query: string) {
  if (!query || query === "") {
    return text;
  }
  const regex = escapedQueryRegex(query);
  return text.replace(
    regex,
    (match) => `<mark class="query-highlight">${match}</mark>`
  );
}
